import { AuthClient } from '@icp-sdk/auth/client'
import { Signature } from '@icp-sdk/core/agent'
import { Delegation, DelegationChain, Ed25519KeyIdentity } from '@icp-sdk/core/identity'
import { SESSION_LENGTH_MS } from '@misc/constants'
import { keychainManager } from '@plug-core/keyring'

const IDENTITY_PROVIDER_URLS = {
  legacy: 'https://identity.ic0.app',
  v2: 'https://id.ai',
}

let authClient: AuthClient | null = null
let authClientIdentity: Ed25519KeyIdentity

export const setAuthClient = async (identity: Ed25519KeyIdentity) => {
  authClientIdentity = identity
  authClient = await AuthClient.create({ identity })
}

export const connectII = async (type: 'legacy' | 'v2'): Promise<{ principal: string }> => {
  return new Promise((resolve, reject) => {
    if (!authClient) {
      reject(new Error('Initialize authClient first'))
      return
    }

    authClient.login({
      identityProvider: IDENTITY_PROVIDER_URLS[type],
      maxTimeToLive: BigInt(SESSION_LENGTH_MS * 1000 * 1000),
      onSuccess: async (successResponse) => {
        try {
          const identity = authClient!.getIdentity()
          const principal = identity.getPrincipal()

          if (principal.isAnonymous()) {
            reject(new Error('Failed to get principal ID from II'))
          }

          const delegationChain = DelegationChain.fromDelegations(
            successResponse.delegations.map(({ delegation, signature }) => ({
              delegation: new Delegation(
                delegation.pubkey,
                delegation.expiration,
                delegation.targets
              ),
              signature: signature as Signature,
            })),
            successResponse.userPublicKey
          )

          await keychainManager.setDelegationKeychain(
            authClientIdentity,
            delegationChain,
            principal.toString()
          )

          resolve({ principal: principal.toString() })
        } catch (error) {
          reject(error)
        }
      },
      onError: (error) => reject(error),
    })
  })
}

export const disconnectII = async (): Promise<void> => {
  const client = authClient || (await AuthClient.create())
  await client.logout()
}
